import { useEffect, useRef, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Section } from '@/shared/ui/layout/Section';
import { Button } from '@/shared/ui/buttons/Button';
import { Input } from '@/shared/ui/inputs/Input';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { ConfirmDialog } from '@/shared/ui/dialogs/ConfirmDialog';
import { SplitStatusChip } from '@/shared/ui/chips/SplitStatusChip';
import { apiSplitsDelete, apiSplitsSave, useSplitDetail } from '@/entities/splits';
import { apiParcelsProductsPut, useParcelProducts } from '@/entities/parcels-products';
import { usePricingConfig } from '@/entities/pricing';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { usePerm } from '@/shared/hooks/usePerm';
import { extractErrorMessage } from '@/shared/api/errors';
import { normalizeDecimal } from '@/shared/utils/number';
import { formatUsd } from '@/shared/utils/currency';
import { queryKeys } from '@/shared/api/queryKeys';
import type { ApiSplitsSaveBody, ApiSplitsSaveTarget } from '@/shared/types/api/splits';
import type { ApiParcelsProductsPostBody } from '@/shared/types/api/parcels-products';
import { PinflCard } from '@/widgets/split-distribute/modules/ui/PinflCard';
import {
  allocQtyInvalid,
  productPriceInvalid,
  productQtyInvalid,
  type FormValues,
  type TargetForm,
} from '@/widgets/split-distribute/modules/ui/model';

export const WidgetsSplitDistributeMain = () => {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const toast = useToast();
  const navigate = useNavigate();

  const detail = useSplitDetail(id);
  const split = detail.data;
  const parcelId = split?.parcel.id ?? '';

  const productsQuery = useParcelProducts(split?.parcel.id);

  const pricing = usePricingConfig();
  const pricePerKg = Number(normalizeDecimal(pricing.data?.package_price_per_kg ?? '0'));
  const splitFeePerPackage = Number(normalizeDecimal(pricing.data?.split_fee_per_package ?? '0'));

  const canSave = usePerm('splits.distribute');
  const canDelete = usePerm('splits.delete');

  const { control, register, handleSubmit, reset, watch, setValue } = useForm<FormValues>({
    defaultValues: { products: [], targets: [] },
  });
  const productsFA = useFieldArray({ control, name: 'products', keyName: 'fieldId' });
  const targetsFA = useFieldArray({ control, name: 'targets' });

  const [deleteOpen, setDeleteOpen] = useState(false);
  // Field-level errors (red borders) are shown only after a Save attempt.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Expand/collapse state for the PINFL cards lives here, keyed by the card's
  // stable list index. Keeping it in the parent (instead of inside each card)
  // means a re-render triggered by generating a unique id — or by any other
  // field change — can never collapse an open card.
  const [expandedMap, setExpandedMap] = useState<Record<number, boolean>>({});
  const toggleExpanded = (i: number) =>
    setExpandedMap((prev) => ({ ...prev, [i]: !prev[i] }));

  // Seed once per split id (after BOTH detail + parcel products have loaded).
  const seededIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!split || productsQuery.isLoading) return;
    if (seededIdRef.current === split.id) return;
    const products = (productsQuery.data ?? []).map((p) => ({
      id: p.id,
      full_name: p.full_name,
      quantity: p.quantity,
      price_per_item: Number(p.price_per_item),
    }));
    const targets: TargetForm[] = split.targets.map((tg) => ({
      serverId: tg.id,
      pinfl: tg.pinfl,
      first_name: tg.first_name,
      last_name: tg.last_name,
      middle_name: tg.middle_name,
      phone_number: tg.phone_number,
      passport_series: tg.passport_series,
      address_name: tg.address_name,
      region: tg.region,
      city: tg.city,
      street: tg.street,
      building: tg.building,
      apartment: tg.apartment,
      additional_info: tg.additional_info,
      package_name: tg.package_name,
      // Price is left blank on load so the field shows the computed-from-weight
      // value as a placeholder; an admin only fills it to override.
      package_price: '',
      package_weight: tg.package_weight,
      package_weight_unit: tg.package_weight_unit,
      saved_package: tg.package_id
        ? {
            price: Number(tg.package_price) || 0,
            weight: String(tg.package_weight ?? ''),
            unit: tg.package_weight_unit,
          }
        : null,
      allocations: tg.allocations.map((a) => ({ product_id: a.product_id, quantity: a.quantity })),
    }));
    reset({ products, targets });
    seededIdRef.current = split.id;
  }, [split, productsQuery.data, productsQuery.isLoading, reset]);

  // Persist product edits (name, quantity, price) to the canonical
  // parcel-products record.
  const persistProduct = useMutate({
    api: (vars: { productId: string; body: ApiParcelsProductsPostBody }) =>
      apiParcelsProductsPut(parcelId, vars.productId, vars.body),
    invalidateKeys: [queryKeys.parcels.products(parcelId), queryKeys.pinfl.all],
    onError: (err) => toast.error(extractErrorMessage(err)),
  });

  const saveMutation = useMutate({
    api: (body: ApiSplitsSaveBody) => apiSplitsSave(id, body),
    invalidateKeys: [
      queryKeys.splits.all,
      queryKeys.splits.detail(id),
      queryKeys.splits.pendingCount,
      // Approval moves PINFL limit usage from the owner to the recipients.
      queryKeys.pinfl.all,
    ],
    onSuccess: () => {
      toast.success(t('splits.save.success'));
      navigate('/splits');
    },
    onError: (err) => toast.error(extractErrorMessage(err, t('splits.save.errors.saveFailed'))),
  });

  const deleteMutation = useMutate({
    api: () => apiSplitsDelete(id),
    invalidateKeys: [queryKeys.splits.all, queryKeys.splits.pendingCount, queryKeys.pinfl.all],
    onSuccess: () => {
      toast.success(t('splits.save.deleteSuccess'));
      navigate('/splits');
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });

  if (detail.isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }
  if (detail.isError || !split) {
    return (
      <div className="p-6">
        <ErrorState
          title={t('splits.detail.loadError')}
          message={extractErrorMessage(detail.error, t('splits.detail.loadError'))}
          onRetry={() => void detail.refetch()}
        />
      </div>
    );
  }

  const readOnly = split.status !== 'DRAFT' && split.status !== 'IN_PROGRESS';
  const saving = saveMutation.isPending;

  const liveProducts = watch('products') ?? [];
  const liveTargets = watch('targets') ?? [];

  // ---- Validation ----
  // Distribution must be EXACT: every product's total allocation across ALL
  // PINFLs must equal its (possibly admin-edited) quantity. productOverMap now
  // flags products whose total != quantity; it drives the red highlight in the
  // PINFL allocation rows and blocks saving.
  const productOverMap = new Map<string, boolean>();
  const misallocatedNames: string[] = [];
  for (const p of liveProducts) {
    let total = 0;
    for (const tg of liveTargets) {
      for (const al of tg.allocations ?? []) {
        if (al.product_id === p.id) total += Number(al.quantity) || 0;
      }
    }
    const misallocated = total !== (Number(p.quantity) || 0);
    productOverMap.set(p.id, misallocated);
    if (misallocated) misallocatedNames.push(p.full_name);
  }
  const anyMisallocated = misallocatedNames.length > 0;
  const anyPinflEmpty = liveTargets.some((tg) => !tg.pinfl?.trim());
  const anyPackageNameEmpty = liveTargets.some((tg) => !tg.package_name?.trim());
  const hasErrors = anyMisallocated || anyPinflEmpty || anyPackageNameEmpty;
  // Invalid numeric fields (blank / out-of-range) — enforced on Save only.
  const anyProductFieldInvalid = liveProducts.some(
    (p) => productQtyInvalid(p.quantity) || productPriceInvalid(p.price_per_item),
  );
  const anyAllocInvalid = liveTargets.some((tg) =>
    (tg.allocations ?? []).some((al) => allocQtyInvalid(al.quantity)),
  );
  const hasFieldErrors = anyProductFieldInvalid || anyAllocInvalid;

  // Service fee is computed server-side as targets × split_fee_per_package; we
  // mirror that here purely for read-only display.
  const serviceFeeTotal = targetsFA.fields.length * splitFeePerPackage;

  // Persist any product edit (name, quantity, price) on blur. A blank/invalid
  // value is NOT persisted and NOT rewritten in the field — it keeps whatever
  // the admin typed and is flagged red on Save instead.
  const handleProductBlur = (index: number, productId: string) => {
    const record = (productsQuery.data ?? []).find((p) => p.id === productId);
    if (!record) return;
    const name = (watch(`products.${index}.full_name`) ?? '').toString().trim();
    const qtyValue = watch(`products.${index}.quantity`);
    const priceValue = watch(`products.${index}.price_per_item`);
    if (productQtyInvalid(qtyValue) || productPriceInvalid(priceValue)) return;
    const full_name = name || record.full_name;
    const quantity = Number(qtyValue);
    const price_per_item = Number(normalizeDecimal(String(priceValue)));
    if (
      full_name === record.full_name &&
      quantity === record.quantity &&
      price_per_item === record.price_per_item
    ) {
      return;
    }
    persistProduct.mutate({
      productId,
      body: {
        category_id: record.category_id,
        subcategory_id: record.subcategory_id,
        full_name,
        quantity,
        price_per_item,
      },
    });
  };

  const addTarget = () => {
    const prods = watch('products') ?? [];
    targetsFA.append({
      pinfl: '',
      first_name: '',
      last_name: '',
      middle_name: '',
      phone_number: '',
      passport_series: '',
      address_name: '',
      region: 'TASHKENT_CITY',
      city: '',
      street: '',
      building: '',
      apartment: '',
      additional_info: '',
      package_name: '',
      package_price: '',
      package_weight: '',
      package_weight_unit: 'kg',
      allocations: prods.length === 1 ? [{ product_id: prods[0]?.id ?? '', quantity: 1 }] : [],
    });
  };

  const onSubmit = handleSubmit((values) => {
    setSubmitAttempted(true);
    if (hasFieldErrors) {
      toast.error(t('splits.save.errors.saveFailed'));
      return;
    }
    if (hasErrors) {
      toast.error(
        anyMisallocated
          ? t('splits.save.errors.distributionExact', { products: misallocatedNames.join(', ') })
          : t('splits.save.errors.saveFailed'),
      );
      return;
    }
    const targets: ApiSplitsSaveTarget[] = values.targets.map((tg) => {
      // Optional override: a non-empty entry is sent as the explicit package
      // price; an empty entry sends null so the backend computes it from weight.
      const priceRaw = normalizeDecimal((tg.package_price ?? '').toString()).trim();
      const priceNum = Number(priceRaw);
      const package_price = priceRaw !== '' && Number.isFinite(priceNum) ? priceNum : null;
      return {
        ...(tg.serverId ? { id: tg.serverId } : {}),
        pinfl: tg.pinfl,
        first_name: tg.first_name,
        last_name: tg.last_name,
        middle_name: tg.middle_name,
        phone_number: tg.phone_number,
        passport_series: tg.passport_series,
        address_name: tg.address_name,
        region: tg.region,
        city: tg.city,
        street: tg.street,
        building: tg.building,
        apartment: tg.apartment,
        additional_info: tg.additional_info,
        package_name: tg.package_name,
        package_price,
        package_weight: Number(tg.package_weight) || 0,
        package_weight_unit: tg.package_weight_unit,
        allocations: (tg.allocations ?? [])
          .filter((a) => (Number(a.quantity) || 0) > 0)
          .map((a) => ({ product_id: a.product_id, quantity: Number(a.quantity) })),
      };
    });
    const body: ApiSplitsSaveBody = { targets };
    saveMutation.mutate(body);
  });

  return (
    <>
      <PageHeader
        title={split.parcel.trek_id || split.parcel.name}
        subtitle={t('splits.detail.trek', { trek: split.parcel.trek_id })}
        showBack
        backTo="/splits"
        right={<SplitStatusChip status={split.status} />}
      />
      <div className="space-y-4 px-4 py-4 lg:px-6 lg:py-6">
        {readOnly && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {t('splits.save.readOnlyNote')}
          </div>
        )}

        {/* SECTION 1 — Products */}
        <Section title={t('splits.save.sections.products')}>
          {productsFA.fields.length === 0 ? (
            <p className="text-sm text-ink-muted">{t('splits.empty.products')}</p>
          ) : (
            productsFA.fields.map((field, i) => {
              const nameReg = register(`products.${i}.full_name`);
              const qtyReg = register(`products.${i}.quantity`);
              const priceReg = register(`products.${i}.price_per_item`);
              const liveProduct = liveProducts[i];
              const qtyError =
                submitAttempted && productQtyInvalid(liveProduct?.quantity)
                  ? t('parcels.products.errors.quantityMin')
                  : undefined;
              const priceError =
                submitAttempted && productPriceInvalid(liveProduct?.price_per_item)
                  ? t('parcels.products.errors.priceMin')
                  : undefined;
              const onBlur = () => handleProductBlur(i, field.id);
              return (
                <div
                  key={field.fieldId}
                  className="flex flex-col gap-2 sm:flex-row sm:items-start"
                >
                  <div className="flex-1">
                    <Input
                      label={t('splits.save.product.name')}
                      disabled={readOnly}
                      {...nameReg}
                      onBlur={(e) => {
                        void nameReg.onBlur(e);
                        onBlur();
                      }}
                    />
                  </div>
                  <div className="w-full sm:w-28">
                    <Input
                      type="number"
                      inputMode="numeric"
                      label={t('splits.save.product.quantity')}
                      disabled={readOnly}
                      error={qtyError}
                      {...qtyReg}
                      onBlur={(e) => {
                        void qtyReg.onBlur(e);
                        onBlur();
                      }}
                    />
                  </div>
                  <div className="w-full sm:w-36">
                    <Input
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      label={t('splits.save.product.unitPrice')}
                      disabled={readOnly}
                      error={priceError}
                      {...priceReg}
                      onBlur={(e) => {
                        void priceReg.onBlur(e);
                        onBlur();
                      }}
                    />
                  </div>
                </div>
              );
            })
          )}
        </Section>

        {/* SECTION 2 — Recipients */}
        <Section
          title={t('splits.save.sections.recipients')}
          action={
            !readOnly ? (
              <Button
                variant="outline"
                size="sm"
                disabled={saving}
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={addTarget}
              >
                {t('splits.save.addPinfl')}
              </Button>
            ) : undefined
          }
        >
          {targetsFA.fields.length === 0 ? (
            <p className="text-sm text-ink-muted">{t('splits.empty.targets')}</p>
          ) : (
            targetsFA.fields.map((field, index) => (
              <PinflCard
                key={field.id}
                index={index}
                control={control}
                register={register}
                setValue={setValue}
                watch={watch}
                products={liveProducts}
                parcelId={parcelId}
                productOverMap={productOverMap}
                pricePerKg={pricePerKg}
                expanded={Boolean(expandedMap[index])}
                onToggle={() => toggleExpanded(index)}
                onRemove={() => targetsFA.remove(index)}
                disabled={saving}
                readOnly={readOnly}
                submitAttempted={submitAttempted}
              />
            ))
          )}
        </Section>

        {/* SECTION 3 — Service fee (computed, read-only) */}
        <Section title={t('splits.save.sections.serviceFee')}>
          <p className="text-sm text-ink">
            {t('splits.save.serviceFeeComputed', {
              total: formatUsd(serviceFeeTotal),
              count: targetsFA.fields.length,
              fee: formatUsd(splitFeePerPackage),
            })}
          </p>
        </Section>

        {anyMisallocated && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {t('splits.save.errors.distributionExact', { products: misallocatedNames.join(', ') })}
          </div>
        )}

        {/* Bottom actions */}
        {!readOnly && (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {canDelete && (
              <Button
                variant="danger"
                disabled={saving || deleteMutation.isPending}
                leftIcon={<Trash2 className="h-4 w-4" />}
                onClick={() => setDeleteOpen(true)}
              >
                {t('splits.save.delete')}
              </Button>
            )}
            {canSave && (
              <Button
                variant="primary"
                loading={saving}
                disabled={saving}
                onClick={() => void onSubmit()}
              >
                {t('splits.save.submit')}
              </Button>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteOpen}
        title={t('splits.save.deleteConfirmTitle')}
        description={t('splits.save.deleteConfirmDescription')}
        confirmLabel={t('splits.save.delete')}
        confirmVariant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onCancel={() => setDeleteOpen(false)}
      />
    </>
  );
};
